import { useState, useEffect } from 'react'
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api'
import AppointmentPreferenceView from '../AppointmentPreferenceView'

import { states } from '@forwardflow/helpers/utils/states'
import { Input, message, Select, Skeleton } from 'antd'
import { UserService } from '@forwardflow/services/UserService'
import { ENV } from '@forwardflow/helpers/configs'
import { useGetUserDetailsByEmail } from '@forwardflow/hooks/customHooks/organization/useGetUserDetailsByEmail'

interface AddressData {
   addressLine1: string
   street: string
   city: string
   state: string
}

interface VirtualData {
   platform: string
   url: string
   password: string
   meetingId: string
}

const libraries: any = ['places']

const AppointmentPreference = ({ email }: { email: string }) => {
   const {
      data: userDetails,
      isLoading,
      mutate,
   } = useGetUserDetailsByEmail(email || '')

   const [selectedPreference, setSelectedPreference] = useState<
      'PHONE-CALL' | 'IN-PERSON' | 'VIRTUAL'
   >('PHONE-CALL')
   const [addressData, setAddressData] = useState<AddressData>({
      addressLine1: '',
      street: '',
      city: '',
      state: '',
   })
   const [virtualData, setVirtualData] = useState<VirtualData>({
      platform: 'Zoom',
      url: '',
      password: '',
      meetingId: '',
   })
   const [isViewMode, setIsViewMode] = useState(true)

   const { isLoaded } = useJsApiLoader({
      id: 'google-map-script',
      googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY,
      libraries,
   })

   const [autocomplete, setAutocomplete] =
      useState<google.maps.places.Autocomplete | null>(null)

   const onLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
      setAutocomplete(autocompleteInstance)
   }

   const onPlaceChanged = () => {
      if (autocomplete !== null) {
         const place = autocomplete.getPlace()
         handlePlaceSelect(place)
      }
   }

   useEffect(() => {
      resetFormToApiData()
   }, [userDetails])

   useEffect(() => {
      console.log(
         'Google Maps API Key:',
         ENV.GOOGLE_MAPS_API_KEY ? 'Present' : 'Missing'
      )

      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
         console.log('Google Maps already loaded')
      }
   }, [])

   useEffect(() => {
      if (userDetails) {
         console.log('User details received:', userDetails)
      }
   }, [userDetails])

   const handlePreferenceChange = (
      preference: 'PHONE-CALL' | 'IN-PERSON' | 'VIRTUAL'
   ) => {
      setSelectedPreference(preference)
   }

   const handleAddressChange = (field: keyof AddressData, value: string) => {
      setAddressData((prev) => ({
         ...prev,
         [field]: value,
      }))
   }

   const handleVirtualDataChange = (
      field: keyof VirtualData,
      value: string
   ) => {
      setVirtualData((prev) => ({
         ...prev,
         [field]: value,
      }))
   }

   const handlePlaceSelect = (place: any) => {
      console.log('Selected place:', place)

      // Extract address components
      const addressComponents = place.address_components || []
      const formattedAddress = place.formatted_address || ''

      let street = ''
      let city = ''
      let state = ''

      addressComponents.forEach((component: any) => {
         const types = component.types

         if (types.includes('street_number') || types.includes('route')) {
            street += component.long_name + ' '
         }
         if (
            types.includes('locality') ||
            types.includes('administrative_area_level_2')
         ) {
            city = component.long_name
         }
         if (types.includes('administrative_area_level_1')) {
            state = component.short_name
         }
      })

      setAddressData({
         addressLine1: formattedAddress,
         street: street.trim(),
         city: city,
         state: state,
      })
   }

   const resetFormToApiData = () => {
      if (userDetails?.appointmentData) {
         const preference = userDetails.appointmentData

         // Set preference type
         if (preference.appointmentType) {
            setSelectedPreference(
               preference.meetingPlatform === 'phone-call'
                  ? 'PHONE-CALL'
                  : (preference.appointmentType.toUpperCase() as
                       | 'PHONE-CALL'
                       | 'IN-PERSON'
                       | 'VIRTUAL')
            )
         } else {
            setSelectedPreference('PHONE-CALL')
         }

         // Set address data
         if (preference.meetingAddress) {
            setAddressData({
               addressLine1: preference.meetingAddress.address || '',
               street: preference.meetingAddress.street || '',
               city: preference.meetingAddress.city || '',
               state: preference.meetingAddress.state || '',
            })
         } else {
            setAddressData({
               addressLine1: '',
               street: '',
               city: '',
               state: '',
            })
         }

         // Set virtual data
         if (preference.meetingPlatform) {
            setVirtualData({
               platform:
                  preference.meetingPlatform === 'phone-call'
                     ? 'ZOOM'
                     : preference.meetingPlatform === 'zoom'
                     ? 'ZOOM'
                     : 'GOOGLE-MEET',
               url: preference.meetingLink || '',
               password: preference.meetingPasscode || '',
               meetingId: preference.meetingId || '',
            })
         } else {
            setVirtualData({
               platform: 'Zoom',
               url: '',
               password: '',
               meetingId: '',
            })
         }
      } else {
         // Fallback to defaults
         setSelectedPreference('PHONE-CALL')
         setAddressData({
            addressLine1: '',
            street: '',
            city: '',
            state: '',
         })
         setVirtualData({
            platform: 'Zoom',
            url: '',
            password: '',
            meetingId: '',
         })
      }
   }

   // Update handleCancel
   const handleCancel = () => {
      resetFormToApiData()
      console.log('Cancel clicked - form reset to original values')
   }

   const handleDone = async () => {
      console.log('Done clicked', {
         email,
         selectedPreference,
         addressData,
         virtualData,
      })

      // Validate meeting link for virtual appointments
      if (selectedPreference === 'VIRTUAL' && !virtualData.url.trim()) {
         message.error('Meeting link is required for virtual appointments')
         return
      }

      try {
         UserService.updateAppointmentPreference({
            email,
            selectedPreference,
            addressData,
            virtualData,
         })
         message.success('Appointment preference updated successfully')
         console.log('Appointment preference updated successfully')
         mutate() // Refresh user details after update
      } catch (error) {
         message.error('Error updating appointment preference')
         console.error('Error updating appointment preference:', error)
      }
      setIsViewMode(true)
   }

   const handleEdit = () => {
      setIsViewMode(false)
   }

   if (isLoading) {
      return (
         <div className="text-white">
            <Skeleton active paragraph={{ rows: 2 }} title={false}></Skeleton>
         </div>
      )
   }

   if (isViewMode) {
      return (
         <AppointmentPreferenceView
            selectedPreference={selectedPreference}
            addressData={addressData}
            virtualData={virtualData}
            onEdit={handleEdit}
         />
      )
   }
   const options = [
      {
         value: 'PHONE-CALL',
         label: 'Phone call',
      },
      {
         value: 'IN-PERSON',
         label: 'In-person',
      },
      {
         value: 'VIRTUAL',
         label: 'Virtual',
      },
   ]

   const virtualOptions = [
      { value: 'GOOGLE-MEET', label: 'Google Meet' },
      { value: 'ZOOM', label: 'Zoom' },
   ]
   return (
      <div className="bg-primary/10 text-white p-6   min-w-[500px]">
         <div className="space-y-6">
            {/* Preference Selection Dropdown */}
            <div className="flex items-center justify-between border4 borderless">
               <Select
                  value={selectedPreference}
                  options={options}
                  onChange={handlePreferenceChange}
                  className="w-48 !rounded text-sm cursor-pointer !h-10 bg-primary/10 pl-4"
               ></Select>

               {/* Action Buttons */}
               <div className="flex justify-end gap-3">
                  <button
                     className="h-10 px-6 text-sm font-medium text-gray-400 transition-colors rounded-[4px] hover:text-white hover:bg-slate-600 bg-primary/10"
                     onClick={handleCancel}
                  >
                     Cancel
                  </button>
                  <button
                     className="h-10 px-6 text-sm font-medium text-white transition-colors bg-primary rounded-[4px] hover:bg-primary-dark"
                     onClick={handleDone}
                  >
                     Done
                  </button>
               </div>
            </div>

            {/* In-person Address Form */}
            {selectedPreference === 'IN-PERSON' && (
               <div className="space-y-4">
                  <div>
                     <label className="block mb-2 text-xs font-medium text-gray-400">
                        Address
                     </label>
                     {isLoaded ? (
                        <Autocomplete
                           onLoad={onLoad}
                           onPlaceChanged={onPlaceChanged}
                           options={{
                              types: ['address'],
                              componentRestrictions: { country: 'us' },
                           }}
                        >
                           <input
                              type="text"
                              value={addressData.addressLine1}
                              onChange={(e) => {
                                 setAddressData((prev) => ({
                                    ...prev,
                                    addressLine1: e.target.value,
                                 }))
                              }}
                              placeholder="Start typing your address..."
                              style={{
                                 width: '100%',
                                 padding: '8px 12px',
                                 fontSize: '14px',
                                 color: 'white',
                                 backgroundColor:
                                    'rgba(var(--primary-rgb), 0.1)',
                                 border: 'none',
                                 borderRadius: '6px',
                                 minHeight: '40px',
                              }}
                              className="w-full outline-none px-3 text-sm text-white placeholder !min-h-10 !border-none rounded-md !bg-primary/10"
                           />
                        </Autocomplete>
                     ) : (
                        <Input
                           type="text"
                           placeholder="Loading address search..."
                           disabled
                           className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                        />
                     )}
                  </div>

                  <div className="hidden">
                     <label className="block mb-2 text-xs font-medium text-gray-400">
                        Address Line 1
                     </label>
                     <Input
                        type="text"
                        name="address-line1"
                        placeholder="Address Line 1"
                        value={addressData.addressLine1}
                        onChange={(e) =>
                           handleAddressChange('addressLine1', e.target.value)
                        }
                        autoComplete="address-line1"
                        className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                     />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                     <div>
                        <label className="block mb-2 text-xs font-medium text-gray-400">
                           Street
                        </label>
                        <Input
                           type="text"
                           name="address-line2"
                           placeholder="Street"
                           value={addressData.street}
                           onChange={(e) =>
                              handleAddressChange('street', e.target.value)
                           }
                           autoComplete="address-line2"
                           className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                        />
                     </div>

                     <div>
                        <label className="block mb-2 text-xs font-medium text-gray-400">
                           City
                        </label>
                        <Input
                           type="text"
                           name="city"
                           placeholder="City"
                           value={addressData.city}
                           onChange={(e) =>
                              handleAddressChange('city', e.target.value)
                           }
                           autoComplete="address-level2"
                           className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                        />
                     </div>

                     <div className="border4 borderless">
                        <label className="block mb-2 text-xs font-medium text-gray-400">
                           State
                        </label>
                        <Select
                           value={addressData.state}
                           onChange={(e) =>
                              handleAddressChange('state', e as string)
                           }
                           placeholder="Select State"
                           showSearch
                           allowClear
                           options={states}
                           className="w-full !rounded text-sm cursor-pointer !h-10 bg-primary/10 pl-4"
                        ></Select>
                     </div>
                  </div>
               </div>
            )}

            {/* Virtual Meeting Options */}
            {selectedPreference === 'VIRTUAL' && (
               <div className="mt-5 space-y-5">
                  {/* Platform Selection Dropdown */}
                  <div className="flex items-center gap-4 border4 borderless">
                     <Select
                        value={virtualData.platform}
                        options={virtualOptions}
                        onChange={(e) =>
                           handleVirtualDataChange('platform', e as string)
                        }
                        className="w-48 !rounded text-sm cursor-pointer !h-10 bg-primary/10 pl-4"
                     ></Select>

                     <Input
                        type="text"
                        placeholder="Enter Meeting Link *"
                        value={virtualData.url}
                        onChange={(e) =>
                           handleVirtualDataChange('url', e.target.value)
                        }
                        className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                     />
                  </div>

                  {/* Meeting Password */}
                  <div>
                     <label className="block mb-2 text-xs font-medium text-gray-400">
                        Meeting Password
                     </label>
                     <Input
                        type="text"
                        placeholder="Enter Meeting Password"
                        value={virtualData.password}
                        onChange={(e) =>
                           handleVirtualDataChange('password', e.target.value)
                        }
                        className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                     />
                  </div>

                  {/* Meeting ID */}
                  <div>
                     <label className="block mb-2 text-xs font-medium text-gray-400">
                        Meeting ID
                     </label>
                     <Input
                        type="text"
                        placeholder="Enter Meeting ID"
                        value={virtualData.meetingId}
                        onChange={(e) =>
                           handleVirtualDataChange('meetingId', e.target.value)
                        }
                        className="w-full px-3 text-sm text-white placeholder-gray-500 !min-h-10 !border-none rounded-md !bg-primary/10"
                     />
                  </div>
               </div>
            )}
         </div>
      </div>
   )
}

export default AppointmentPreference
